// =====================================================================
// Catalogo unico de steps do onboarding (admin + agent).
// Mantido em sync com:
//  - Backend: FieldService.Business.Consts.OnboardingStepCodes (C#)
//  - Backend: Mexx.BackgroundTasks.IntegrationEvents.EventHandling.Onboarding.OnboardingProgressHandler
// Plug novos steps SEMPRE neste arquivo + atualizar backend.
// =====================================================================

export const STEP_IDS = {
    CREATE_FIRST_TICKET: 'create-first-ticket',
    ASSUME_TICKET: 'assume-ticket',
    CLOSE_TICKET: 'close-ticket',
    CREATE_AGENT_USER: 'create-agent-user',
    CREATE_ORGANIZATION: 'create-organization',
    CONFIGURE_ORGANIZATION: 'configure-organization',
    CREATE_CATEGORY: 'create-category',
    CREATE_TEAM: 'create-team',
    LINK_TEAM_MEMBERS: 'link-team-members',
    LEARN_AUTOMATION_TEMPLATES: 'learn-automation-templates',
    CLASSIFY_FINISHED_TICKET: 'classify-finished-ticket',
};

export const AUDIENCES = {
    ADMIN: 'admin',
    AGENT: 'agent',
    BOTH: 'both',
    REQUESTER: 'requester',
};

// =====================================================================
// IDs dos tours (usados em STEP_CATALOG.tourId e em TOURS_BY_ID)
// =====================================================================
export const TOUR_IDS = {
    SYSTEM_OVERVIEW: 'system-overview',
    CREATE_FIRST_TICKET: 'create-first-ticket',
    CREATE_FIRST_TICKET_REQUESTER: 'create-first-ticket-requester',
    ASSUME_TICKET: 'assume-ticket',
    CLOSE_TICKET: 'close-ticket',
    CREATE_AGENT_USER: 'create-agent-user',
    CREATE_ORGANIZATION: 'create-organization',
    CONFIGURE_ORGANIZATION: 'configure-organization',
    CREATE_CATEGORY: 'create-category',
    CREATE_TEAM: 'create-team',
    LINK_TEAM_MEMBERS: 'link-team-members',
    LEARN_AUTOMATION_TEMPLATES: 'learn-automation-templates',
    CLASSIFY_FINISHED_TICKET: 'classify-finished-ticket',
};

// =====================================================================
// MENU PATHS — caminhos de navegacao do sidebar pra cada tour.
// Renderizados pelo componente <TourMenuPath> dentro do balao do step intro
// pra orientar o usuario sobre como chegar manualmente na tela do tour.
// Cada node: { iconName, labelKey } resolvido via intl.formatMessage.
// =====================================================================
export const MENU_PATHS = {
    CREATE_FIRST_TICKET: [
        { iconName: 'ticket',  labelKey: 'onboarding.menuPath.calleds' },
        { iconName: 'plus',    labelKey: 'onboarding.menuPath.newTicket' },
    ],
    ASSUME_TICKET: [
        { iconName: 'ticket',  labelKey: 'onboarding.menuPath.calleds' },
        { iconName: 'list',    labelKey: 'onboarding.menuPath.calledsList' },
        { iconName: 'hand',    labelKey: 'onboarding.menuPath.assumeIcon' },
    ],
    CLOSE_TICKET: [
        { iconName: 'ticket',  labelKey: 'onboarding.menuPath.calleds' },
        { iconName: 'list',    labelKey: 'onboarding.menuPath.calledsList' },
        { iconName: 'edit',    labelKey: 'onboarding.menuPath.openInProgress' },
    ],
    CREATE_AGENT_USER: [
        { iconName: 'users',   labelKey: 'onboarding.menuPath.userControl' },
        { iconName: 'user',    labelKey: 'onboarding.menuPath.agents' },
        { iconName: 'plus',    labelKey: 'onboarding.menuPath.newAgent' },
    ],
    CREATE_ORGANIZATION: [
        { iconName: 'organization', labelKey: 'onboarding.menuPath.organizationsMenu' },
        { iconName: 'organization', labelKey: 'onboarding.menuPath.organization' },
        { iconName: 'plus',         labelKey: 'onboarding.menuPath.add' },
    ],
    CONFIGURE_ORGANIZATION: [
        { iconName: 'organization', labelKey: 'onboarding.menuPath.organizationsMenu' },
        { iconName: 'select',       labelKey: 'onboarding.menuPath.selectOrganization' },
        { iconName: 'edit',         labelKey: 'onboarding.menuPath.editOrganization' },
    ],
    CREATE_CATEGORY: [
        { iconName: 'organization', labelKey: 'onboarding.menuPath.organizationsMenu' },
        { iconName: 'category',     labelKey: 'onboarding.menuPath.organizationCategories' },
        { iconName: 'plus',         labelKey: 'onboarding.menuPath.add' },
    ],
    CREATE_TEAM: [
        { iconName: 'teamWork', labelKey: 'onboarding.menuPath.serviceGroup' },
        { iconName: 'team',     labelKey: 'onboarding.menuPath.teams' },
        { iconName: 'plus',     labelKey: 'onboarding.menuPath.add' },
    ],
    LINK_TEAM_MEMBERS: [
        { iconName: 'teamWork', labelKey: 'onboarding.menuPath.serviceGroup' },
        { iconName: 'team',     labelKey: 'onboarding.menuPath.teams' },
        { iconName: 'select',   labelKey: 'onboarding.menuPath.selectTeam' },
    ],
    LEARN_AUTOMATION_TEMPLATES: [
        { iconName: 'automation', labelKey: 'onboarding.menuPath.automations' },
        { iconName: 'template',   labelKey: 'onboarding.menuPath.templatesButton' },
    ],
    CLASSIFY_FINISHED_TICKET: [
        { iconName: 'category', labelKey: 'onboarding.menuPath.calledClassifications' },
    ],
};

// =====================================================================
// Configuracao rica por step (UI + navegacao).
// labelKey / descriptionKey apontam para chaves do _i18n/translations.json.
// Componentes resolvem via intl.formatMessage({ id: labelKey }).
// navigateTo: pode ser string OU funcao (demoSeedRefs) => string|null
//   - se funcao retornar null o checklist-fab nao navega (e nao inicia tour).
// =====================================================================
export const STEP_CATALOG = [
    {
        id: STEP_IDS.CREATE_FIRST_TICKET,
        audience: AUDIENCES.BOTH,
        order: 1,
        icon: 'AddCircleOutline',
        labelKey: 'onboarding.step.createFirstTicket.label',
        descriptionKey: 'onboarding.step.createFirstTicket.description',
        navigateTo: '/pt/called-registration',
        tourId: TOUR_IDS.CREATE_FIRST_TICKET,
        includeForRequester: true,
        requesterTourId: TOUR_IDS.CREATE_FIRST_TICKET_REQUESTER,
        requesterNavigateTo: '/pt/called-registration-client',
    },
    {
        id: STEP_IDS.ASSUME_TICKET,
        audience: AUDIENCES.BOTH,
        order: 2,
        icon: 'PanToolOutlined',
        labelKey: 'onboarding.step.assumeTicket.label',
        descriptionKey: 'onboarding.step.assumeTicket.description',
        navigateTo: '/pt/calleds',
        tourId: TOUR_IDS.ASSUME_TICKET,
        // Step depende de chamado existente (is_demo OU real via fallback).
        // ChecklistFab oculta este step se demoSeedRefs.chamadoNovoId == null
        // (ambiente dedicated recem-criado sem dados nenhum).
        requiresDemoRef: 'chamadoNovoId',
    },
    {
        id: STEP_IDS.CLOSE_TICKET,
        audience: AUDIENCES.BOTH,
        order: 3,
        icon: 'CheckCircleOutline',
        labelKey: 'onboarding.step.closeTicket.label',
        descriptionKey: 'onboarding.step.closeTicket.description',
        // Usa o chamado JA ENCERRADO do seed — status ja esta Encerrado, entao
        // force-click no Salvar abre o modal de confirmacao naturalmente, sem
        // precisar de etapa extra de "mude o status pra Encerrado" no tour.
        navigateTo: (refs) =>
            refs?.chamadoEncerradoId
                ? `/pt/called-accompaniment/${refs.chamadoEncerradoId}`
                : '/pt/calleds',
        tourId: TOUR_IDS.CLOSE_TICKET,
        // Step depende de chamado encerrado (is_demo OU real via fallback).
        requiresDemoRef: 'chamadoEncerradoId',
    },
    {
        id: STEP_IDS.CREATE_AGENT_USER,
        audience: AUDIENCES.ADMIN,
        order: 4,
        icon: 'PersonAddOutlined',
        labelKey: 'onboarding.step.createAgentUser.label',
        descriptionKey: 'onboarding.step.createAgentUser.description',
        navigateTo: '/pt/user-registration',
        tourId: TOUR_IDS.CREATE_AGENT_USER,
    },
    {
        id: STEP_IDS.CREATE_ORGANIZATION,
        audience: AUDIENCES.ADMIN,
        order: 5,
        icon: 'BusinessOutlined',
        labelKey: 'onboarding.step.createOrganization.label',
        descriptionKey: 'onboarding.step.createOrganization.description',
        navigateTo: '/pt/organization-registration',
        tourId: TOUR_IDS.CREATE_ORGANIZATION,
    },
    {
        id: STEP_IDS.CONFIGURE_ORGANIZATION,
        audience: AUDIENCES.ADMIN,
        order: 6,
        icon: 'TuneOutlined',
        labelKey: 'onboarding.step.configureOrganization.label',
        descriptionKey: 'onboarding.step.configureOrganization.description',
        // A rota organization-edit NAO recebe :id na URL — a organizacao alvo e
        // lida do Redux state (state.organizations.organization). Se nao estiver
        // populado, OrganizationEdit faz `document.location.href=/{lang}/calleds`
        // (reload full page) — bug original que enviava o usuario pra lista de
        // chamados em vez da edicao da org.
        navigateTo: '/pt/organization-edit',
        // prepare e chamado pelo ChecklistFab ANTES do history.push.
        // Busca a org demo via API e dispatcha ADD_CURRENT no Redux pra
        // OrganizationEdit encontrar a org no state ja no mount.
        prepare: async (refs, dispatch) => {
            if (!refs?.organizacaoId) return;
            try {
                const orgService = await import('../../../services/organization.service');
                const orgActions = await import('../../../redux/actions/organizations');
                const result = await orgService.getById(refs.organizacaoId);
                const org = result?.data || result;
                if (org && org.id) {
                    dispatch(orgActions.addCurrent(org));
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[Onboarding] Falha ao preparar org pro tour Configurar:', err);
            }
        },
        tourId: TOUR_IDS.CONFIGURE_ORGANIZATION,
        // Step depende de organizacao existente (is_demo OU real via fallback).
        requiresDemoRef: 'organizacaoId',
    },
    {
        id: STEP_IDS.CREATE_CATEGORY,
        audience: AUDIENCES.ADMIN,
        order: 7,
        icon: 'CategoryOutlined',
        labelKey: 'onboarding.step.createCategory.label',
        descriptionKey: 'onboarding.step.createCategory.description',
        // Rota corrigida: a tela "categoria de organizacao" e o que o usuario espera —
        // tem combo Organizacao + Nome + SLA, etc. /category-registration era global.
        navigateTo: '/pt/organizationcategory-registration',
        tourId: TOUR_IDS.CREATE_CATEGORY,
    },
    {
        id: STEP_IDS.CREATE_TEAM,
        audience: AUDIENCES.ADMIN,
        order: 8,
        icon: 'GroupOutlined',
        labelKey: 'onboarding.step.createTeam.label',
        descriptionKey: 'onboarding.step.createTeam.description',
        navigateTo: '/pt/techinicalteam-registration',
        tourId: TOUR_IDS.CREATE_TEAM,
    },
    {
        id: STEP_IDS.LINK_TEAM_MEMBERS,
        audience: AUDIENCES.ADMIN,
        order: 9,
        icon: 'GroupWorkOutlined',
        labelKey: 'onboarding.step.linkTeamMembers.label',
        descriptionKey: 'onboarding.step.linkTeamMembers.description',
        // Mesmo padrao do CONFIGURE_ORGANIZATION: rota nao recebe :id, time vem
        // do Redux state.techinicalteams.techinicalteam. Sem prepare/dispatch,
        // TechinicalTeamEdit faz document.location.href=/lang/calleds (reload full).
        navigateTo: '/pt/techinicalteam-edit',
        // Busca time demo + dispatch addCurrent ANTES do history.push, igual
        // ao CONFIGURE_ORGANIZATION. Service de team nao tem getById, uso Api
        // direto no padrao /techinicalteams/{id} (mesma convencao do delete).
        prepare: async (refs, dispatch) => {
            if (!refs?.timeId) return;
            try {
                const Api = (await import('../../../services/api')).default;
                const teamActions = await import('../../../redux/actions/techinicalteams');
                const result = await Api.get(`/techinicalteams/${refs.timeId}`);
                const team = result?.data?.data || result?.data || result;
                if (team && team.id) {
                    dispatch(teamActions.addCurrent(team));
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[Onboarding] Falha ao preparar time pro tour Associar:', err);
            }
        },
        tourId: TOUR_IDS.LINK_TEAM_MEMBERS,
        // Step depende de time existente (is_demo OU real via fallback).
        requiresDemoRef: 'timeId',
    },
    {
        id: STEP_IDS.LEARN_AUTOMATION_TEMPLATES,
        audience: AUDIENCES.ADMIN,
        order: 10,
        icon: 'BoltOutlined',
        labelKey: 'onboarding.step.learnAutomationTemplates.label',
        descriptionKey: 'onboarding.step.learnAutomationTemplates.description',
        navigateTo: '/pt/automation-templates',
        tourId: TOUR_IDS.LEARN_AUTOMATION_TEMPLATES,
    },
    {
        id: STEP_IDS.CLASSIFY_FINISHED_TICKET,
        audience: AUDIENCES.REQUESTER,
        order: 11,
        icon: 'StarOutline',
        labelKey: 'onboarding.step.classifyFinishedTicket.label',
        descriptionKey: 'onboarding.step.classifyFinishedTicket.description',
        navigateTo: '/pt/calledclassifications',
        tourId: TOUR_IDS.CLASSIFY_FINISHED_TICKET,
    },
];

export const TOTAL_STEPS_BY_AUDIENCE = {
    admin: STEP_CATALOG.filter((s) => s.audience === AUDIENCES.ADMIN || s.audience === AUDIENCES.BOTH).length,
    agent: STEP_CATALOG.filter((s) => s.audience === AUDIENCES.AGENT || s.audience === AUDIENCES.BOTH).length,
    requester: STEP_CATALOG.filter((s) => s.audience === AUDIENCES.REQUESTER || s.includeForRequester === true).length,
};

// =====================================================================
// Resolve navigateTo dinamico (string OU funcao(demoSeedRefs)).
// Retorna string final ou null se a funcao indicar que nao ha alvo
// (ex.: tenant sem demoSeedRefs pra esse step).
// =====================================================================
export function resolveNavigateTo(step, demoSeedRefs, audience) {
    if (!step) return null;
    const navigateTo = (audience === AUDIENCES.REQUESTER && step.requesterNavigateTo)
        ? step.requesterNavigateTo
        : step.navigateTo;
    if (typeof navigateTo === 'function') {
        try {
            return navigateTo(demoSeedRefs || {});
        } catch (_) {
            return null;
        }
    }
    return navigateTo || null;
}

// =====================================================================
// Detectacao: usuario esta em plano TRIAL? (legacy fallback)
// Hoje o gate principal vem do backend (state.onboarding.audience).
// =====================================================================
const CONTRACT_STATUS_TRIAL = 5;

export function isUserOnTrial(user) {
    if (!user) return false;
    const status =
        user.contractstatusid ??
        user.contract_status_id ??
        user.contractStatusId ??
        null;
    return status === CONTRACT_STATUS_TRIAL;
}

// =====================================================================
// Detectacao de rotas onde overlays de onboarding (welcome modal, FAB,
// drawer de checklist) NAO devem aparecer:
//   - rotas de autenticacao / pre-login
//   - rota de portal externo do cliente (/client-portal-external/*) — tela
//     publica acessada por link, nao faz parte do dashboard interno.
// =====================================================================
const AUTH_ROUTE_TOKENS = [
    '/login',
    '/forgot-password',
    '/adm-password-reset',
    '/global-adm-password-reset',
    '/admin/forgot-password',
    '/admin/login',
    '/two-factor',
    '/confirm-account',
    '/client-portal-external',
];

export function isAuthRoute(pathname) {
    if (!pathname || typeof pathname !== 'string') return false;
    const lower = pathname.toLowerCase();
    return AUTH_ROUTE_TOKENS.some((token) => lower.includes(token));
}

// =====================================================================
// Detectacao de audience baseada no userSession.user.userrole
// Roles por role_type (enum RoleType, estavel entre hirers):
//   0 GlobalAdministrator | 1 Administrator | 2 Technical | 3 Client | 4 Coordenator
// NAO usar role.id: e gerado por sequence e diverge por hirer em bancos
// compartilhados (so o primeiro hirer do banco recebe os ids fixos 1-7).
// =====================================================================
const ADMIN_ROLE_TYPES = new Set([0, 1, 4]);
const AGENT_ROLE_TYPES = new Set([2]);
const REQUESTER_ROLE_TYPES = new Set([3]);

export function detectAudience(user) {
    if (!user) return AUDIENCES.ADMIN;

    const roles = user.userrole ?? user.userRole ?? user.userroles ?? [];
    if (!Array.isArray(roles) || roles.length === 0) return AUDIENCES.ADMIN;

    const types = roles
        .map((r) => (typeof r === 'object' ? r.role?.roletype ?? r.roletype ?? r.roleType ?? r.role_type : Number(r)))
        .filter((type) => Number.isFinite(type));

    const isAdmin = types.some((type) => ADMIN_ROLE_TYPES.has(type));
    if (isAdmin) return AUDIENCES.ADMIN;

    const isAgent = types.some((type) => AGENT_ROLE_TYPES.has(type));
    if (isAgent) return AUDIENCES.AGENT;

    const isRequester = types.some((type) => REQUESTER_ROLE_TYPES.has(type));
    if (isRequester) return AUDIENCES.REQUESTER;

    return AUDIENCES.ADMIN;
}

export function filterStepsByAudience(audience) {
    if (audience === AUDIENCES.ADMIN) {
        return STEP_CATALOG.filter(
            (s) => s.audience === AUDIENCES.ADMIN || s.audience === AUDIENCES.BOTH
        );
    }
    if (audience === AUDIENCES.AGENT) {
        return STEP_CATALOG.filter(
            (s) => s.audience === AUDIENCES.AGENT || s.audience === AUDIENCES.BOTH
        );
    }
    if (audience === AUDIENCES.REQUESTER) {
        return STEP_CATALOG.filter(
            (s) => s.audience === AUDIENCES.REQUESTER || s.includeForRequester === true
        );
    }
    return STEP_CATALOG;
}

// =====================================================================
// Wait-for-target v2 — usa MutationObserver em vez de polling.
//
// Vantagens vs v1 (setInterval 100ms):
//   - reage instantaneamente a mudancas no DOM (sem janela de espera)
//   - PERFORMANCE: zero impacto enquanto nao houver tour ativo
//     (observer so e criado quando waitForTarget e chamado)
//   - aguarda "DOM estavel" (sem mudancas no ramo do alvo por stabilityMs)
//     evitando avancar pro proximo step antes da pagina hidratar de vez
//
// IMPORTANTE: NAO requer mudanca nas paginas alvo. Toda deteccao fica
// isolada aqui. Telas criticas (chamados, accompaniment) NAO recebem
// codigo novo — so atributos data-tour="..." (peso zero, atributo HTML puro).
// =====================================================================
export function waitForTarget(selector, { timeout = 8000 } = {}) {
    return new Promise((resolve) => {
        if (typeof document === 'undefined' || !selector) {
            resolve(false);
            return;
        }

        const isVisible = (el) =>
            !!el && (el.offsetParent !== null || el.getClientRects().length > 0);

        let observer = null;
        let timeoutId = null;
        let resolved = false;

        const cleanup = () => {
            if (observer) { observer.disconnect(); observer = null; }
            if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
        };

        const tryResolve = (value) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve(value);
        };

        // Resolve assim que o alvo aparece visivel. Removido o stabilityMs
        // (250ms sem mutacoes) que travava em prod: SignalR + BreakWorkTimer +
        // notification polling mutam o DOM constantemente, resetando o timer
        // de estabilidade infinitamente — waitForTarget nunca resolvia e o
        // WaitingOverlay ficava preso ate o timeout de 60s.
        const check = () => {
            const el = document.querySelector(selector);
            if (isVisible(el)) tryResolve(true);
        };

        // Check imediato — pode ja estar pronto
        check();
        if (resolved) return;

        observer = new MutationObserver(check);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
        });

        timeoutId = setTimeout(() => tryResolve(false), timeout);
    });
}

/**
 * Retorna o primeiro target "real" (nao body/null) do tour.
 * Usado pra waitForTarget antes de iniciar — garante que a pagina alvo
 * esta montada e o elemento existe no DOM.
 */
export function getFirstRealTargetOfTour(tour) {
    if (!tour || !Array.isArray(tour.steps)) return null;
    const realStep = tour.steps.find(
        (s) => s.target && s.target !== 'body' && typeof s.target === 'string'
    );
    return realStep ? realStep.target : null;
}

// =====================================================================
// Helper: tour DEMO/fallback (intro + outro centrados, sem targets dinamicos).
// Usado quando demoSeedRefs nao tem o ID requerido pelo tour completo.
// =====================================================================
function buildDemoFallbackTour(tourId, audience, introKey, outroKey, menuPath) {
    return {
        id: tourId,
        audience,
        steps: [
            {
                target: 'body',
                placement: 'center',
                disableBeacon: true,
                menuPath,
                titleKey: `${introKey}.title`,
                contentKey: `${introKey}.content`,
            },
            {
                target: 'body',
                placement: 'center',
                disableBeacon: true,
                titleKey: `${outroKey}.title`,
                contentKey: `${outroKey}.content`,
            },
        ],
    };
}

// =====================================================================
// TOUR: Criar primeiro chamado (12 steps)
// Ordem dos campos confirmada pelo usuario:
//   prioridade > organizacao > categoria > localizacao > solicitante >
//   tipo > titulo > descricao > anexos > salvar
// Ancoras esperadas em /pt/called-registration:
//   data-tour="called-form-priority"
//   data-tour="called-form-organization"
//   data-tour="called-form-category"
//   data-tour="called-form-location"
//   data-tour="called-form-requester"
//   data-tour="called-form-type"
//   data-tour="called-form-title"
//   data-tour="called-form-description"
//   data-tour="called-form-attachments"
//   data-tour="called-form-submit"
// =====================================================================
export const TOUR_CREATE_FIRST_TICKET = {
    id: TOUR_IDS.CREATE_FIRST_TICKET,
    audience: AUDIENCES.BOTH,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CREATE_FIRST_TICKET,
            titleKey: 'onboarding.tour.createFirstTicket.intro.title',
            contentKey: 'onboarding.tour.createFirstTicket.intro.content',
        },
        {
            target: '[data-tour="called-form-priority"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.priority.title',
            contentKey: 'onboarding.tour.createFirstTicket.priority.content',
        },
        {
            target: '[data-tour="called-form-organization"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.organization.title',
            contentKey: 'onboarding.tour.createFirstTicket.organization.content',
        },
        {
            target: '[data-tour="called-form-category"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.category.title',
            contentKey: 'onboarding.tour.createFirstTicket.category.content',
        },
        {
            target: '[data-tour="called-form-location"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.location.title',
            contentKey: 'onboarding.tour.createFirstTicket.location.content',
        },
        {
            target: '[data-tour="called-form-requester"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.requester.title',
            contentKey: 'onboarding.tour.createFirstTicket.requester.content',
        },
        {
            target: '[data-tour="called-form-type"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.type.title',
            contentKey: 'onboarding.tour.createFirstTicket.type.content',
        },
        {
            target: '[data-tour="called-form-title"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.titleField.title',
            contentKey: 'onboarding.tour.createFirstTicket.titleField.content',
        },
        {
            target: '[data-tour="called-form-description"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.description.title',
            contentKey: 'onboarding.tour.createFirstTicket.description.content',
        },
        {
            target: '[data-tour="called-form-attachments"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.attachments.title',
            contentKey: 'onboarding.tour.createFirstTicket.attachments.content',
        },
        {
            target: '[data-tour="called-form-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.submit.title',
            contentKey: 'onboarding.tour.createFirstTicket.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicket.outro.title',
            contentKey: 'onboarding.tour.createFirstTicket.outro.content',
        },
    ],
};

export const TOUR_CREATE_FIRST_TICKET_REQUESTER = {
    id: TOUR_IDS.CREATE_FIRST_TICKET_REQUESTER,
    audience: AUDIENCES.REQUESTER,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CREATE_FIRST_TICKET,
            titleKey: 'onboarding.tour.createFirstTicketRequester.intro.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.intro.content',
        },
        {
            target: '[data-tour="called-client-form-organization"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.organization.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.organization.content',
        },
        {
            target: '[data-tour="called-client-form-location"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.location.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.location.content',
        },
        {
            target: '[data-tour="called-client-form-title"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.titleField.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.titleField.content',
        },
        {
            target: '[data-tour="called-client-form-description"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.description.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.description.content',
        },
        {
            target: '[data-tour="called-client-form-attachments"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.attachments.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.attachments.content',
        },
        {
            target: '[data-tour="called-client-form-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.submit.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createFirstTicketRequester.outro.title',
            contentKey: 'onboarding.tour.createFirstTicketRequester.outro.content',
        },
    ],
};

export const TOUR_CLASSIFY_FINISHED_TICKET = {
    id: TOUR_IDS.CLASSIFY_FINISHED_TICKET,
    audience: AUDIENCES.REQUESTER,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CLASSIFY_FINISHED_TICKET,
            titleKey: 'onboarding.tour.classifyFinishedTicket.intro.title',
            contentKey: 'onboarding.tour.classifyFinishedTicket.intro.content',
        },
        {
            target: '[data-tour="called-classification-table"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.classifyFinishedTicket.table.title',
            contentKey: 'onboarding.tour.classifyFinishedTicket.table.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.classifyFinishedTicket.outro.title',
            contentKey: 'onboarding.tour.classifyFinishedTicket.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: Assumir chamado (apenas DESTACA — sem force-click)
//
// Por que sem force-click?
//   Assumir chamado dispara fluxo real (modal de confirmacao + atribui agente
//   no banco). Forcar isso programaticamente pode atrapalhar o usuario que
//   talvez ja tenha clicado, ou nao quer assumir esse chamado especifico.
//   Tour apenas demonstra ONDE clicar — a acao fica a criterio do usuario.
//
// Fluxo (com chamadoNovoId):
//   1 intro centrado + menuPath
//   2 destaca a row do chamado novo demo
//   3 destaca a maozinha (sem forcar click)
//   4 outro centrado
// Sem chamadoNovoId: tour demo fallback.
// =====================================================================
export function buildAssumeTicketTour(chamadoNovoId) {
    if (!chamadoNovoId) {
        return buildDemoFallbackTour(
            TOUR_IDS.ASSUME_TICKET,
            AUDIENCES.BOTH,
            'onboarding.tour.assumeTicket.demo.intro',
            'onboarding.tour.assumeTicket.demo.outro',
            MENU_PATHS.ASSUME_TICKET,
        );
    }

    const rowSelector = `[data-tour="called-row-${chamadoNovoId}"]`;
    const handSelector = `[data-tour="called-row-assume-button-${chamadoNovoId}"]`;

    return {
        id: TOUR_IDS.ASSUME_TICKET,
        audience: AUDIENCES.BOTH,
        steps: [
            {
                target: 'body',
                placement: 'center',
                disableBeacon: true,
                menuPath: MENU_PATHS.ASSUME_TICKET,
                titleKey: 'onboarding.tour.assumeTicket.intro.title',
                contentKey: 'onboarding.tour.assumeTicket.intro.content',
            },
            {
                target: rowSelector,
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.assumeTicket.row.title',
                contentKey: 'onboarding.tour.assumeTicket.row.content',
            },
            {
                target: handSelector,
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.assumeTicket.hand.title',
                contentKey: 'onboarding.tour.assumeTicket.hand.content',
            },
            {
                target: 'body',
                placement: 'center',
                disableBeacon: true,
                titleKey: 'onboarding.tour.assumeTicket.outro.title',
                contentKey: 'onboarding.tour.assumeTicket.outro.content',
            },
        ],
    };
}

// =====================================================================
// TOUR: Encerrar chamado (refeito — usa chamado JA ENCERRADO do seed)
// Rota /pt/called-accompaniment/{chamadoEncerradoId} — apesar do nome,
// e a tela de EDICAO do chamado.
//
// Por que chamado ENCERRADO e nao "em andamento"?
//   Status ja esta Encerrado no seed (chamado #3), entao force-click no
//   Salvar dispara o modal de confirmacao de encerramento naturalmente.
//
// Fluxo completo (com modal CalledFinish):
//   DoneAllIcon -> finishCalled -> submitAction -> abre <CalledFinish> via
//   TransitionsModal (state.modalopenFinish). CalledFinish e um StepWizard:
//   ActionStep (sempre) -> [status?] -> [checklist?] -> [material?] -> handleSubmit.
//   No caso simples do seed, ActionStep ja contem o botao final que dispara
//   o handleSubmit -> chamado encerrado.
//
// Steps:
//   1 intro + menuPath
//   2 destaca Time (na tela de edit)
//   3 destaca Status (na tela de edit)
//   4 destaca DoneAllIcon; onAdvance forca click -> abre modal CalledFinish
//   5 destaca campo Acao (ReactQuill dentro do modal)
//   6 destaca radio publico/privado
//   7 destaca botao Salvar/Finalizar (sem force-click — usuario escolhe encerrar)
//   8 outro
//
// Ancoras:
//   /pt/called-accompaniment/{id}:
//     data-tour="accompaniment-form-team"
//     data-tour="accompaniment-form-status"
//     data-tour="accompaniment-form-finish"  (IconButton DoneAllIcon)
//   Dentro do modal CalledFinish/ActionStep:
//     data-tour="called-finish-action"   (Grid do ReactQuill)
//     data-tour="called-finish-privacy"  (Grid do Radio publica/privada)
//     data-tour="called-finish-submit"   (Grid do Button saveAction -> finaliza)
// =====================================================================
export function buildCloseTicketTour(chamadoEncerradoId) {
    if (!chamadoEncerradoId) {
        return buildDemoFallbackTour(
            TOUR_IDS.CLOSE_TICKET,
            AUDIENCES.BOTH,
            'onboarding.tour.closeTicket.demo.intro',
            'onboarding.tour.closeTicket.demo.outro',
            MENU_PATHS.CLOSE_TICKET,
        );
    }

    const finishSelector = '[data-tour="accompaniment-form-finish"]';
    const finishActionSelector = '[data-tour="called-finish-action"]';

    return {
        id: TOUR_IDS.CLOSE_TICKET,
        audience: AUDIENCES.BOTH,
        steps: [
            {
                target: 'body',
                placement: 'center',
                disableBeacon: true,
                menuPath: MENU_PATHS.CLOSE_TICKET,
                titleKey: 'onboarding.tour.closeTicket.intro.title',
                contentKey: 'onboarding.tour.closeTicket.intro.content',
            },
            {
                target: '[data-tour="accompaniment-form-team"]',
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.closeTicket.team.title',
                contentKey: 'onboarding.tour.closeTicket.team.content',
            },
            {
                target: '[data-tour="accompaniment-form-status"]',
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.closeTicket.status.title',
                contentKey: 'onboarding.tour.closeTicket.status.content',
            },
            {
                target: finishSelector,
                placement: 'auto',
                disableBeacon: true,
                spotlightClicks: true,
                titleKey: 'onboarding.tour.closeTicket.finish.title',
                contentKey: 'onboarding.tour.closeTicket.finish.content',
                // Forca click no DoneAllIcon -> abre modal CalledFinish.
                // Aguarda o campo Acao aparecer (primeiro elemento do StepWizard).
                onAdvance: {
                    clickSelector: finishSelector,
                    waitForSelector: finishActionSelector,
                    waitTimeoutMs: 6000,
                },
            },
            {
                target: finishActionSelector,
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.closeTicket.action.title',
                contentKey: 'onboarding.tour.closeTicket.action.content',
            },
            {
                target: '[data-tour="called-finish-privacy"]',
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.closeTicket.privacy.title',
                contentKey: 'onboarding.tour.closeTicket.privacy.content',
            },
            {
                target: '[data-tour="called-finish-submit"]',
                placement: 'auto',
                disableBeacon: true,
                titleKey: 'onboarding.tour.closeTicket.finalize.title',
                contentKey: 'onboarding.tour.closeTicket.finalize.content',
            },
            {
                target: 'body',
                placement: 'center',
                disableBeacon: true,
                titleKey: 'onboarding.tour.closeTicket.outro.title',
                contentKey: 'onboarding.tour.closeTicket.outro.content',
            },
        ],
        // Ao concluir/cancelar/fechar o tour, fecha o modal CalledFinish
        // (se ainda estiver aberto). Clica no botao Cancelar do ActionStep
        // que dispara closeModal -> closeModalFinish -> setState modalopenFinish=false.
        onCleanup: () => {
            const cancelBtn = document.querySelector('[data-tour="called-finish-cancel"]');
            if (cancelBtn) {
                try { cancelBtn.click(); } catch (_) { /* swallow */ }
            }
        },
    };
}

export function buildConfigureOrganizationTour(organizacaoId) {
    if (!organizacaoId) {
        return buildDemoFallbackTour(
            TOUR_IDS.CONFIGURE_ORGANIZATION,
            AUDIENCES.ADMIN,
            'onboarding.tour.configureOrganization.demo.intro',
            'onboarding.tour.configureOrganization.demo.outro',
            MENU_PATHS.CONFIGURE_ORGANIZATION,
        );
    }
    return TOUR_CONFIGURE_ORGANIZATION;
}

export function buildLinkTeamMembersTour(timeId) {
    if (!timeId) {
        return buildDemoFallbackTour(
            TOUR_IDS.LINK_TEAM_MEMBERS,
            AUDIENCES.ADMIN,
            'onboarding.tour.linkTeam.demo.intro',
            'onboarding.tour.linkTeam.demo.outro',
            MENU_PATHS.LINK_TEAM_MEMBERS,
        );
    }
    return TOUR_LINK_TEAM_MEMBERS;
}

// =====================================================================
// TOUR: Criar agente
// Ancoras esperadas em /pt/user-registration:
//   data-tour="user-form-name"
//   data-tour="user-form-email"
//   data-tour="user-form-role"
//   data-tour="user-form-expediente"
//   data-tour="user-form-start-page"
//   data-tour="user-form-submit"
// =====================================================================
export const TOUR_CREATE_AGENT_USER = {
    id: TOUR_IDS.CREATE_AGENT_USER,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CREATE_AGENT_USER,
            titleKey: 'onboarding.tour.createAgent.intro.title',
            contentKey: 'onboarding.tour.createAgent.intro.content',
        },
        {
            target: '[data-tour="user-form-name"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createAgent.name.title',
            contentKey: 'onboarding.tour.createAgent.name.content',
        },
        {
            target: '[data-tour="user-form-email"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createAgent.email.title',
            contentKey: 'onboarding.tour.createAgent.email.content',
        },
        {
            target: '[data-tour="user-form-role"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createAgent.role.title',
            contentKey: 'onboarding.tour.createAgent.role.content',
        },
        {
            target: '[data-tour="user-form-start-page"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createAgent.startPage.title',
            contentKey: 'onboarding.tour.createAgent.startPage.content',
        },
        {
            target: '[data-tour="user-form-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createAgent.submit.title',
            contentKey: 'onboarding.tour.createAgent.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createAgent.outro.title',
            contentKey: 'onboarding.tour.createAgent.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: Criar organizacao
// Ancoras esperadas em /pt/organization-registration:
//   data-tour="organization-form-group"
//   data-tour="organization-form-segment"
//   data-tour="organization-form-name"
//   data-tour="organization-form-submit"
// =====================================================================
export const TOUR_CREATE_ORGANIZATION = {
    id: TOUR_IDS.CREATE_ORGANIZATION,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CREATE_ORGANIZATION,
            titleKey: 'onboarding.tour.createOrganization.intro.title',
            contentKey: 'onboarding.tour.createOrganization.intro.content',
        },
        {
            target: '[data-tour="organization-form-group"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createOrganization.group.title',
            contentKey: 'onboarding.tour.createOrganization.group.content',
        },
        {
            target: '[data-tour="organization-form-segment"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createOrganization.segment.title',
            contentKey: 'onboarding.tour.createOrganization.segment.content',
        },
        {
            target: '[data-tour="organization-form-name"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createOrganization.name.title',
            contentKey: 'onboarding.tour.createOrganization.name.content',
        },
        {
            target: '[data-tour="organization-form-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createOrganization.submit.title',
            contentKey: 'onboarding.tour.createOrganization.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createOrganization.outro.title',
            contentKey: 'onboarding.tour.createOrganization.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: Configurar organizacao (manual marking)
// Rota /pt/organization-edit (sem :id — org vem do Redux via prepare).
//
// Fluxo: intro com menuPath, destaca aba Configurações (default),
// percorre os 9 campos defaults da Configuration (componente Wizard step 0),
// depois destaca abas Endereco e SLA SEM clicar, outro.
//
// Ancoras na aba Configurações (wizard-pages/configuration/index.js):
//   data-tour="org-config-type"         (Tipo de chamado padrão)
//   data-tour="org-config-origin"       (Origem padrão)
//   data-tour="org-config-status"       (Status padrão)
//   data-tour="org-config-team"         (Time padrão)
//   data-tour="org-config-technical"    (Técnico padrão)
//   data-tour="org-config-priority"     (Prioridade padrão)
//   data-tour="org-config-expire"       (Tempo de expiração)
//   data-tour="org-config-informative"  (Campo informativo - ReactQuill)
//   data-tour="org-config-flags"        (Bloco de Tabs com flags/checkboxes)
// Ancoras nas abas (organization-edit/index.js):
//   data-tour="organization-edit-tab-config"
//   data-tour="organization-edit-tab-address"
//   data-tour="organization-edit-tab-sla"
// =====================================================================
export const TOUR_CONFIGURE_ORGANIZATION = {
    id: TOUR_IDS.CONFIGURE_ORGANIZATION,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CONFIGURE_ORGANIZATION,
            titleKey: 'onboarding.tour.configureOrganization.intro.title',
            contentKey: 'onboarding.tour.configureOrganization.intro.content',
        },
        {
            target: '[data-tour="organization-edit-tab-config"]',
            placement: 'bottom',
            disableBeacon: true,
            titleKey: 'onboarding.tour.configureOrganization.config.title',
            contentKey: 'onboarding.tour.configureOrganization.config.content',
        },
        {
            target: '[data-tour="org-config-type"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.type.title',
            contentKey: 'onboarding.tour.configureOrganization.field.type.content',
        },
        {
            target: '[data-tour="org-config-origin"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.origin.title',
            contentKey: 'onboarding.tour.configureOrganization.field.origin.content',
        },
        {
            target: '[data-tour="org-config-status"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.status.title',
            contentKey: 'onboarding.tour.configureOrganization.field.status.content',
        },
        {
            target: '[data-tour="org-config-team"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.team.title',
            contentKey: 'onboarding.tour.configureOrganization.field.team.content',
        },
        {
            target: '[data-tour="org-config-technical"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.technical.title',
            contentKey: 'onboarding.tour.configureOrganization.field.technical.content',
        },
        {
            target: '[data-tour="org-config-priority"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.priority.title',
            contentKey: 'onboarding.tour.configureOrganization.field.priority.content',
        },
        {
            target: '[data-tour="org-config-expire"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.expire.title',
            contentKey: 'onboarding.tour.configureOrganization.field.expire.content',
        },
        {
            target: '[data-tour="org-config-informative"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.informative.title',
            contentKey: 'onboarding.tour.configureOrganization.field.informative.content',
        },
        {
            target: '[data-tour="org-config-flags"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.configureOrganization.field.flags.title',
            contentKey: 'onboarding.tour.configureOrganization.field.flags.content',
        },
        {
            target: '[data-tour="organization-edit-tab-address"]',
            placement: 'bottom',
            disableBeacon: true,
            titleKey: 'onboarding.tour.configureOrganization.address.title',
            contentKey: 'onboarding.tour.configureOrganization.address.content',
        },
        {
            target: '[data-tour="organization-edit-tab-sla"]',
            placement: 'bottom',
            disableBeacon: true,
            titleKey: 'onboarding.tour.configureOrganization.sla.title',
            contentKey: 'onboarding.tour.configureOrganization.sla.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.configureOrganization.outro.title',
            contentKey: 'onboarding.tour.configureOrganization.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: Criar categoria (rota CORRETA: /pt/organizationcategory-registration)
// Ancoras esperadas:
//   data-tour="org-category-form-organization"  (combo Organizacao)
//   data-tour="org-category-form-name"          (TextField nome)
//   data-tour="org-category-form-submit"        (Button salvar)
// =====================================================================
export const TOUR_CREATE_CATEGORY = {
    id: TOUR_IDS.CREATE_CATEGORY,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CREATE_CATEGORY,
            titleKey: 'onboarding.tour.createCategory.intro.title',
            contentKey: 'onboarding.tour.createCategory.intro.content',
        },
        {
            target: '[data-tour="org-category-form-organization"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createCategory.organization.title',
            contentKey: 'onboarding.tour.createCategory.organization.content',
            // Quando avanca, desmarca o checkbox "template" (vem true por default)
            // pra liberar o campo nome (renderizado dentro de {!state.template && ...}).
            // precondition checa via input.checked — so clica se ainda marcado,
            // evita re-marcar se user ja desmarcou manualmente.
            onAdvance: {
                clickSelector: '[data-tour="org-category-form-template-checkbox"] input[type="checkbox"]',
                precondition: () => {
                    const cb = document.querySelector('[data-tour="org-category-form-template-checkbox"] input[type="checkbox"]');
                    return cb && cb.checked === true;
                },
                waitForSelector: '[data-tour="org-category-form-name"]',
                waitTimeoutMs: 2000,
            },
        },
        {
            target: '[data-tour="org-category-form-name"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createCategory.name.title',
            contentKey: 'onboarding.tour.createCategory.name.content',
        },
        {
            target: '[data-tour="org-category-form-sla-block"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.createCategory.sla.title',
            contentKey: 'onboarding.tour.createCategory.sla.content',
        },
        {
            target: '[data-tour="org-category-form-flags-block"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.createCategory.flags.title',
            contentKey: 'onboarding.tour.createCategory.flags.content',
        },
        {
            target: '[data-tour="org-category-form-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createCategory.submit.title',
            contentKey: 'onboarding.tour.createCategory.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createCategory.outro.title',
            contentKey: 'onboarding.tour.createCategory.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: Criar time
// Ancoras esperadas em /pt/techinicalteam-registration:
//   data-tour="team-form-name"
//   data-tour="team-form-active"
//   data-tour="team-form-submit"
// =====================================================================
export const TOUR_CREATE_TEAM = {
    id: TOUR_IDS.CREATE_TEAM,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.CREATE_TEAM,
            titleKey: 'onboarding.tour.createTeam.intro.title',
            contentKey: 'onboarding.tour.createTeam.intro.content',
        },
        {
            target: '[data-tour="team-form-name"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createTeam.name.title',
            contentKey: 'onboarding.tour.createTeam.name.content',
        },
        {
            target: '[data-tour="team-form-active"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createTeam.active.title',
            contentKey: 'onboarding.tour.createTeam.active.content',
        },
        {
            target: '[data-tour="team-form-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createTeam.submit.title',
            contentKey: 'onboarding.tour.createTeam.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.createTeam.outro.title',
            contentKey: 'onboarding.tour.createTeam.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: Associar membros ao time
// Ancoras esperadas em /pt/techinicalteam-edit/{timeId}:
//   data-tour="team-edit-users-tab"
//   data-tour="team-edit-orgs-tab"
//   data-tour="team-edit-categories-tab"
// =====================================================================
export const TOUR_LINK_TEAM_MEMBERS = {
    id: TOUR_IDS.LINK_TEAM_MEMBERS,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.LINK_TEAM_MEMBERS,
            titleKey: 'onboarding.tour.linkTeam.intro.title',
            contentKey: 'onboarding.tour.linkTeam.intro.content',
        },
        {
            target: '[data-tour="team-edit-users-tab"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.linkTeam.users.title',
            contentKey: 'onboarding.tour.linkTeam.users.content',
        },
        {
            target: '[data-tour="team-edit-orgs-tab"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.linkTeam.orgs.title',
            contentKey: 'onboarding.tour.linkTeam.orgs.content',
        },
        {
            target: '[data-tour="team-edit-categories-tab"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.linkTeam.categories.title',
            contentKey: 'onboarding.tour.linkTeam.categories.content',
        },
        {
            target: '[data-tour="team-edit-submit"]',
            placement: 'auto',
            disableBeacon: true,
            titleKey: 'onboarding.tour.linkTeam.submit.title',
            contentKey: 'onboarding.tour.linkTeam.submit.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.linkTeam.outro.title',
            contentKey: 'onboarding.tour.linkTeam.outro.content',
        },
    ],
};

// =====================================================================
// TOUR: System Overview — primeiro tour exibido apos clicar
// "Iniciar tour" no Welcome Modal. Apresenta a tela principal de
// listagem de chamados (/pt/calleds): topbar, drawers e filtros.
//
// Force click em hamburgers/filtros usa onAdvance.precondition pra
// abrir SOMENTE se estiver fechado (evita fechar drawer ja aberto).
//
// Ancoras esperadas no header-menu/index.js + called-list/index.js:
//   data-tour="topbar-menu-toggle"
//   data-tour="sidebar-left-paper"
//   data-tour="topbar-logo"
//   data-tour="topbar-general-search"
//   data-tour="topbar-search-called-id"
//   data-tour="topbar-reload"
//   data-tour="topbar-notifications"
//   data-tour="topbar-user-menu"
//   data-tour="topbar-actions-toggle"
//   data-tour="drawer-right-actions-paper"
//   data-tour="called-list-filters-toggle"
// =====================================================================
// Anchors INTERNOS dos drawers — wrappers <div data-tour="..."> dentro do Drawer Paper.
// Atributo HTML puro num filho direto e mais confiavel que PaperProps em MUI v5.
const SIDEBAR_LEFT_SELECTOR = '[data-tour="sidebar-left-content"]';
const DRAWER_RIGHT_SELECTOR = '[data-tour="drawer-right-actions-content"]';
const SIDEBAR_TOGGLE_SELECTOR = '[data-tour="topbar-menu-toggle"]';
const ACTIONS_TOGGLE_SELECTOR = '[data-tour="topbar-actions-toggle"]';
const FILTERS_TOGGLE_SELECTOR = '[data-tour="called-list-filters-toggle"]';

// Helper: detecta se um Drawer/Sidebar MUI esta efetivamente VISIVEL no viewport.
//
// MUI Drawer com variant="persistent" mantem o paper SEMPRE no DOM mesmo fechado —
// usa transform: translate pra empurrar pra fora da tela. Por isso offsetParent
// retorna o pai mesmo com drawer fechado. Solucao: getBoundingClientRect +
// checagem de overlap com o viewport.
const isElementInViewport = (selector) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return false;
    const el = document.querySelector(selector);
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return false;
    return rect.right > 0
        && rect.left < window.innerWidth
        && rect.bottom > 0
        && rect.top < window.innerHeight;
};

const isClosed = (selector) => !isElementInViewport(selector);
const isOpen = (selector) => isElementInViewport(selector);

const SIDEBAR_LEFT_CLOSE_SELECTOR = '[data-tour="sidebar-left-close"]';

export const TOUR_SYSTEM_OVERVIEW = {
    id: TOUR_IDS.SYSTEM_OVERVIEW,
    audience: AUDIENCES.BOTH,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.systemOverview.intro.title',
            contentKey: 'onboarding.tour.systemOverview.intro.content',
            // Garante que o icone hamburger esquerdo esteja VISIVEL no proximo step.
            // O hamburger fica oculto (classes.hide) quando o sidebar esta aberto —
            // se aberto, fecha clicando no botao ChevronLeft do drawer header.
            onAdvance: {
                precondition: () => isOpen(SIDEBAR_LEFT_SELECTOR),
                clickSelector: SIDEBAR_LEFT_CLOSE_SELECTOR,
                waitForSelector: SIDEBAR_TOGGLE_SELECTOR,
                waitTimeoutMs: 1500,
            },
        },
        {
            target: SIDEBAR_TOGGLE_SELECTOR,
            placement: 'auto',
            disableBeacon: true,
            spotlightClicks: true,
            titleKey: 'onboarding.tour.systemOverview.menuToggle.title',
            contentKey: 'onboarding.tour.systemOverview.menuToggle.content',
            // Abre sidebar SE estiver fechado, depois aguarda o paper aparecer
            onAdvance: {
                clickSelector: SIDEBAR_TOGGLE_SELECTOR,
                precondition: () => isClosed(SIDEBAR_LEFT_SELECTOR),
                waitForSelector: SIDEBAR_LEFT_SELECTOR,
                waitTimeoutMs: 2000,
            },
        },
        {
            target: SIDEBAR_LEFT_SELECTOR,
            // 'right-start' alinha o tooltip pelo TOPO do anchor (perto do
            // primeiro item do menu — "Chamados") em vez de centralizar no
            // meio do drawer alto. Mais natural visualmente.
            placement: 'right-start',
            disableBeacon: true,
            titleKey: 'onboarding.tour.systemOverview.sidebar.title',
            contentKey: 'onboarding.tour.systemOverview.sidebar.content',
        },
        {
            target: '[data-tour="topbar-general-search"]',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.generalSearch.title',
            contentKey: 'onboarding.tour.systemOverview.generalSearch.content',
        },
        {
            // Spotlight padding reduzido — campo fica entre busca geral e botoes a direita,
            // padding 12 (default) pegava parte dos vizinhos.
            target: '[data-tour="topbar-search-called-id"]',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.searchById.title',
            contentKey: 'onboarding.tour.systemOverview.searchById.content',
        },
        {
            // Mesmo problema do anterior — IconButton pequeno cercado de outros.
            target: '[data-tour="topbar-reload"]',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.reload.title',
            contentKey: 'onboarding.tour.systemOverview.reload.content',
        },
        {
            target: '[data-tour="topbar-notifications"]',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.notifications.title',
            contentKey: 'onboarding.tour.systemOverview.notifications.content',
        },
        {
            target: '[data-tour="topbar-user-menu"]',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.userMenu.title',
            contentKey: 'onboarding.tour.systemOverview.userMenu.content',
        },
        {
            // Step puramente informativo do hamburger Acoes Rapidas — explica o
            // que e, mas NAO abre o drawer automaticamente. Tentativas anteriores
            // (force-click via dispatchEvent + click nativo, e dispatch Redux
            // direto via toggleActionMenu) nao conseguiram abrir o drawer de
            // forma estavel (alguns ambientes ficavam presos no waiting overlay
            // do step do paper). Solucao pragmatica: explicar o icone aqui, o
            // usuario abre quando quiser.
            target: ACTIONS_TOGGLE_SELECTOR,
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.actionsToggle.title',
            contentKey: 'onboarding.tour.systemOverview.actionsToggle.content',
        },
        {
            // Destaca o PRIMEIRO ItemCard da listagem. Cada chamado e um Card
            // (nao DataGrid classica) — o anchor [data-tour="called-row-{id}"]
            // ja existe em todos os ItemCard (adicionado pro tour Assumir),
            // entao o prefix selector pega o primeiro do DOM. Joyride destaca
            // o card real com Solicitante, ID, Titulo, Org, Status, SLA, etc.
            target: '[data-tour^="called-row-"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightPadding: 4,
            titleKey: 'onboarding.tour.systemOverview.columns.title',
            contentKey: 'onboarding.tour.systemOverview.columns.content',
        },
        {
            target: FILTERS_TOGGLE_SELECTOR,
            placement: 'auto',
            disableBeacon: true,
            spotlightClicks: true,
            titleKey: 'onboarding.tour.systemOverview.filters.title',
            contentKey: 'onboarding.tour.systemOverview.filters.content',
            // Abre o accordion de filtros SE estiver fechado
            onAdvance: {
                clickSelector: FILTERS_TOGGLE_SELECTOR,
                precondition: () => {
                    const accordion = document.querySelector(FILTERS_TOGGLE_SELECTOR);
                    return accordion && accordion.getAttribute('aria-expanded') !== 'true';
                },
                waitTimeoutMs: 1000,
            },
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.systemOverview.outro.title',
            contentKey: 'onboarding.tour.systemOverview.outro.content',
        },
    ],
};

// =====================================================================
// TOUR de templates de automacao — primeiro tour interativo (legacy)
// Ancoras na pagina /pt/automation-templates:
//   data-tour="automation-template-preview-button"
//   data-tour="automation-template-use-button"
//   data-tour="automation-template-drawer-close"
// =====================================================================
export const TOUR_LEARN_AUTOMATION_TEMPLATES = {
    id: TOUR_IDS.LEARN_AUTOMATION_TEMPLATES,
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            menuPath: MENU_PATHS.LEARN_AUTOMATION_TEMPLATES,
            titleKey: 'onboarding.tour.learnAutomation.intro.title',
            contentKey: 'onboarding.tour.learnAutomation.intro.content',
        },
        {
            target: '[data-tour="automation-template-preview-button"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightClicks: true,
            titleKey: 'onboarding.tour.learnAutomation.preview.title',
            contentKey: 'onboarding.tour.learnAutomation.preview.content',
            onAdvance: {
                clickSelector: '[data-tour="automation-template-preview-button"]',
                waitForSelector: '[data-tour="automation-template-use-button"]',
                waitTimeoutMs: 4000,
            },
        },
        {
            target: '[data-tour="automation-template-use-button"]',
            placement: 'auto',
            disableBeacon: true,
            spotlightClicks: true,
            titleKey: 'onboarding.tour.learnAutomation.use.title',
            contentKey: 'onboarding.tour.learnAutomation.use.content',
        },
        {
            target: 'body',
            placement: 'center',
            disableBeacon: true,
            titleKey: 'onboarding.tour.learnAutomation.outro.title',
            contentKey: 'onboarding.tour.learnAutomation.outro.content',
        },
    ],
    onCleanup: () => {
        const closeBtn = document.querySelector('[data-tour="automation-template-drawer-close"]');
        if (closeBtn) {
            try { closeBtn.click(); } catch (_) { /* swallow */ }
        }
    },
};

// =====================================================================
// Registry global de tours por id. Tours dinamicos (que precisam injetar
// IDs nos seletores) ficam de fora — ChecklistFab resolve via builder.
// =====================================================================
export const TOURS_BY_ID = {
    [TOUR_SYSTEM_OVERVIEW.id]:            TOUR_SYSTEM_OVERVIEW,
    [TOUR_CREATE_FIRST_TICKET.id]:        TOUR_CREATE_FIRST_TICKET,
    [TOUR_CREATE_FIRST_TICKET_REQUESTER.id]: TOUR_CREATE_FIRST_TICKET_REQUESTER,
    [TOUR_CLASSIFY_FINISHED_TICKET.id]: TOUR_CLASSIFY_FINISHED_TICKET,
    [TOUR_CREATE_AGENT_USER.id]:          TOUR_CREATE_AGENT_USER,
    [TOUR_CREATE_ORGANIZATION.id]:        TOUR_CREATE_ORGANIZATION,
    [TOUR_CONFIGURE_ORGANIZATION.id]:     TOUR_CONFIGURE_ORGANIZATION,
    [TOUR_CREATE_CATEGORY.id]:            TOUR_CREATE_CATEGORY,
    [TOUR_CREATE_TEAM.id]:                TOUR_CREATE_TEAM,
    [TOUR_LINK_TEAM_MEMBERS.id]:          TOUR_LINK_TEAM_MEMBERS,
    [TOUR_LEARN_AUTOMATION_TEMPLATES.id]: TOUR_LEARN_AUTOMATION_TEMPLATES,
};

/**
 * Resolve o tour a partir do tourId concreto (ja decidido — base ou variante
 * de audience, ver resolveTourIdForStep). Nao depende mais de um objeto step.
 *
 * Tours dinamicos sao construidos com base nos demoSeedRefs. Quando o ID
 * requerido nao esta presente (ex.: hirer dedicated sem seed), o builder
 * cai pra tour demo fallback (intro+outro centrados, sem alvos dinamicos).
 *
 * Tours estaticos (sem dependencia de id) sao lidos direto de TOURS_BY_ID.
 */
export function resolveTourById(tourId, demoSeedRefs) {
    if (!tourId) return null;
    const refs = demoSeedRefs || {};

    switch (tourId) {
        case TOUR_IDS.ASSUME_TICKET:
            return buildAssumeTicketTour(refs.chamadoNovoId);
        case TOUR_IDS.CLOSE_TICKET:
            return buildCloseTicketTour(refs.chamadoEncerradoId);
        case TOUR_IDS.CONFIGURE_ORGANIZATION:
            return buildConfigureOrganizationTour(refs.organizacaoId);
        case TOUR_IDS.LINK_TEAM_MEMBERS:
            return buildLinkTeamMembersTour(refs.timeId);
        default:
            return TOURS_BY_ID[tourId] || null;
    }
}

export function resolveTourIdForStep(step, audience) {
    if (!step) return null;
    if (audience === AUDIENCES.REQUESTER && step.requesterTourId) return step.requesterTourId;
    return step.tourId || null;
}
